// src/pages/StudentSlideshowPlayer.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { studentSlideshowAPI, studentTutorialAPI } from '../services/api';
import ReactMarkdown from 'react-markdown';

const stripMarkdownFormatting = (value = '') => String(value)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1');

const escapeRegex = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const HoverableCaption = ({ text = '', translationPairs = [], translationLabel = 'Translation' }) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const cleanText = stripMarkdownFormatting(text);
    const label = String(translationLabel || 'Translation').trim();
    const normalizedLabel = label.endsWith(':') ? label : `${label}:`;

    if (!cleanText) return null;

    const lowerCaption = String(cleanText).toLowerCase();
    const pairs = (translationPairs || [])
        .filter(pair => pair?.term && pair?.translation)
        .filter(pair => {
            const term = String(pair.term).trim().toLowerCase();
            if (!term) return false;
            if (term === lowerCaption) return false; // prevent full-caption translation hover
            return lowerCaption.includes(term);
        })
        .sort((a, b) => b.term.length - a.term.length);

    if (pairs.length === 0) {
        return <ReactMarkdown components={{ p: 'span' }}>{cleanText}</ReactMarkdown>;
    }

    const combinedRegex = new RegExp(`(${pairs.map(pair => escapeRegex(pair.term)).join('|')})`, 'gi');
    const parts = String(cleanText).split(combinedRegex);

    return (
        <>
            {parts.map((part, index) => {
                const pair = pairs.find(p => p.term.toLowerCase() === part.toLowerCase());
                if (!pair) return <span key={`text-${index}`}>{part}</span>;

                return (
                    <span
                        key={`term-${index}`}
                        style={{ position: 'relative', display: 'inline-block', cursor: 'help', fontWeight: '700', color: '#2D4042' }}
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                    >
                        {part}
                        {hoveredIndex === index && (
                            <span style={{
                                position: 'absolute',
                                left: '50%',
                                top: 'calc(100% + 8px)',
                                transform: 'translateX(-50%)',
                                background: '#fff',
                                border: '2px solid #75B9BE',
                                borderRadius: '8px',
                                padding: '8px 10px',
                                boxShadow: '3px 3px 0px rgba(45,64,66,0.12)',
                                whiteSpace: 'normal',
                                minWidth: '200px',
                                maxWidth: '320px',
                                zIndex: 25,
                                fontSize: '0.85em',
                                fontWeight: '700',
                                color: '#2D4042',
                                textAlign: 'left'
                            }}>
                                <div style={{ fontSize: '0.72rem', fontWeight: '800', color: '#5D7375', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                                    {normalizedLabel}
                                </div>
                                {stripMarkdownFormatting(pair.translation)}
                            </span>
                        )}
                    </span>
                );
            })}
        </>
    );
};

const StudentSlideshowPlayer = () => {
    const { studentTutorialId, chunkIndex } = useParams();
    const [slideshow, setSlideshow] = useState(null);
    const [parentHomeworkId, setParentHomeworkId] = useState(null);
    const [studentSettings, setStudentSettings] = useState({ audioEnabled: false, audioSpeed: 1 });
    const [translationApplied, setTranslationApplied] = useState(false);
    const [captionTranslationPairs, setCaptionTranslationPairs] = useState([]);
    const [captionTranslationLabel, setCaptionTranslationLabel] = useState('Translation');
    
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1); 
    
    // +++ FIX: Use Ref to track speed +++
    const speedRef = useRef(playbackSpeed);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

    const getFullUrl = useCallback((path) => {
        if (!path) return '';
        return path.startsWith('http') ? path : `${API_BASE_URL}/${path}`;
    }, [API_BASE_URL]);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            
            const [slideshowRes, tutorialRes] = await Promise.all([
                studentSlideshowAPI.getSlideshowChunk(studentTutorialId, chunkIndex),
                studentTutorialAPI.getById(studentTutorialId).catch(() => ({ data: null }))
            ]);

            if (tutorialRes.data?.studentId) {
                if (typeof tutorialRes.data.studentId === 'object') {
                    const profileSpeed = Number(tutorialRes.data.studentId.audioNarrationSpeed);
                    const initialAudioSpeed = [0.5, 0.75, 1].includes(profileSpeed) ? profileSpeed : 1;
                    setStudentSettings({
                        audioEnabled: tutorialRes.data.studentId.audioNarrationEnabled === true,
                        audioSpeed: initialAudioSpeed
                    });
                    setPlaybackSpeed(initialAudioSpeed);
                } 
            }

            setTranslationApplied(Boolean(tutorialRes.data?.translationApplied));

            const mode = tutorialRes.data?.glossaryLanguageMode || 'PRIMARY_TEXT_ENGLISH_GLOSSARY';
            const showEnglishAsPrimary = mode === 'ENGLISH_TEXT_PRIMARY_GLOSSARY';
            const primaryLanguageLabel = tutorialRes.data?.studentId?.primaryLanguage || tutorialRes.data?.contentLanguage || 'Primary language';
            const sourceChunks = Array.isArray(tutorialRes.data?.generatedContent) ? tutorialRes.data.generatedContent : [];
            const referenceChunks = Array.isArray(tutorialRes.data?.englishGeneratedContent) ? tutorialRes.data.englishGeneratedContent : [];
            const currentChunk = showEnglishAsPrimary
                ? (referenceChunks[Number(chunkIndex)] || sourceChunks[Number(chunkIndex)] || null)
                : (sourceChunks[Number(chunkIndex)] || referenceChunks[Number(chunkIndex)] || null);
            const currentEnglishChunk = showEnglishAsPrimary
                ? (sourceChunks[Number(chunkIndex)] || referenceChunks[Number(chunkIndex)] || null)
                : (referenceChunks[Number(chunkIndex)] || sourceChunks[Number(chunkIndex)] || null);
            const combinedGlossary = (currentChunk?.glossary || []).map((item, index) => ({
                term: item?.term || '',
                translationTerm: currentEnglishChunk?.glossary?.[index]?.term || ''
            }));
            const keywordPairs = combinedGlossary
                .filter(item => item.term && item.translationTerm)
                .map(item => ({ term: item.term, translation: item.translationTerm }));

            setCaptionTranslationPairs(keywordPairs);
            setCaptionTranslationLabel(`${showEnglishAsPrimary ? primaryLanguageLabel : 'English'} translation`);

            if (tutorialRes.data?.homeworkId) setParentHomeworkId(tutorialRes.data.homeworkId);

            if (slideshowRes.data.status === 'COMPLETED') {
                setSlideshow(slideshowRes.data);
                setCurrentSlideIndex(0);
            } else if (['PENDING', 'PROCESSING'].includes(slideshowRes.data.status)) {
                setError(`Generation in progress (${slideshowRes.data.status}).`);
            } else {
                 setError(`Generation failed: ${slideshowRes.data.failureReason || 'Unknown'}`);
            }
        } catch (err) {
             if (err.response?.status === 404) setError('Slideshow not found.');
             else setError('Failed to load slideshow data.');
        } finally {
            setLoading(false);
        }
    }, [studentTutorialId, chunkIndex]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // +++ FIX: Keep speedRef in sync with state +++
    useEffect(() => {
        speedRef.current = playbackSpeed;
        if (audioRef.current) {
            audioRef.current.playbackRate = playbackSpeed;
        }
    }, [playbackSpeed]);

    // Preload Audio
    useEffect(() => {
        if (slideshow?.slides?.[currentSlideIndex]?.audioUrl && audioRef.current) {
            audioRef.current.src = getFullUrl(slideshow.slides[currentSlideIndex].audioUrl);
            // +++ FIX: Use speedRef.current +++
            audioRef.current.playbackRate = speedRef.current;
            audioRef.current.load();
            setIsPlaying(false);
        }
    }, [slideshow, currentSlideIndex, getFullUrl]); 

    const handleNext = () => {
        if (slideshow && currentSlideIndex < slideshow.slides.length - 1) {
            setCurrentSlideIndex(prev => prev + 1);
            if (audioRef.current) { audioRef.current.pause(); setIsPlaying(false); }
        }
    };

    const handlePrev = () => {
        if (currentSlideIndex > 0) {
            setCurrentSlideIndex(prev => prev - 1);
             if (audioRef.current) { audioRef.current.pause(); setIsPlaying(false); }
        }
    };

    const togglePlayPause = () => {
        if (!audioRef.current || !slideshow?.slides[currentSlideIndex]?.audioUrl) return;
        if (isPlaying) audioRef.current.pause();
        else audioRef.current.play().catch(e => console.error(e));
    };

    const cycleSpeed = () => {
        const speeds = [0.75, 1, 1.25, 1.5, 2];
        const nextIdx = (speeds.indexOf(playbackSpeed) + 1) % speeds.length;
        setPlaybackSpeed(speeds[nextIdx]);
    };

    useEffect(() => {
        const el = audioRef.current;
        const stop = () => setIsPlaying(false);
        const start = () => setIsPlaying(true);
        if (el) {
            el.addEventListener('ended', stop);
            el.addEventListener('pause', stop);
            el.addEventListener('play', start);
            return () => {
                el.removeEventListener('ended', stop);
                el.removeEventListener('pause', stop);
                el.removeEventListener('play', start);
            };
        }
    }, []);

    if (loading) return <p>Loading...</p>;
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;
    if (!slideshow?.slides?.length) return <p>No slides.</p>;

    const currentSlide = slideshow.slides[currentSlideIndex];
    const captionText = currentSlide.text || currentSlide.originalText || currentSlide.narration || '';

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '20px auto', border: '1px solid #ccc', borderRadius: '8px', textAlign: 'center' }}>
             {parentHomeworkId && <Link to={`/homework/${parentHomeworkId}`} style={{ display: 'block', marginBottom: '15px', textAlign: 'left' }}>← Back to Homework</Link>}
             
             <h2>Student Slideshow - Chunk {parseInt(chunkIndex) + 1}</h2>

            <div style={{ margin: '20px 0', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                    key={currentSlide.imageUrl}
                    src={getFullUrl(currentSlide.imageUrl)}
                    alt={`Slide ${currentSlideIndex + 1}`}
                    style={{ maxWidth: '100%', maxHeight: '400px', border: '1px solid #eee', borderRadius: '4px' }}
                    onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/600x400/CCCCCC/FFFFFF?text=Image+Load+Error"; }}
                />
            </div>

            <div style={{ minHeight: '4em', background: '#f9f9f9', padding: '10px', borderRadius: '4px', fontSize: '1.1em', lineHeight: '1.5' }}>
                <strong>Narration:</strong> 
                <HoverableCaption
                    text={captionText}
                    translationPairs={translationApplied ? captionTranslationPairs : []}
                    translationLabel={captionTranslationLabel}
                />
            </div>

            <audio ref={audioRef} key={currentSlide.audioUrl} style={{ display: 'none' }} />

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '15px' }}>
                <button onClick={handlePrev} disabled={currentSlideIndex === 0}>Previous</button>
                
                {studentSettings.audioEnabled && (
                    <>
                        <button onClick={togglePlayPause} disabled={!currentSlide.audioUrl} style={{ minWidth: '100px' }}>
                            {isPlaying ? 'Pause' : 'Play Audio'}
                        </button>
                        <button onClick={cycleSpeed} disabled={!currentSlide.audioUrl} style={{ minWidth: '50px' }}>
                            {playbackSpeed}x
                        </button>
                    </>
                )}
                
                <button onClick={handleNext} disabled={currentSlideIndex === slideshow.slides.length - 1}>Next</button>
            </div>
            <p style={{ marginTop: '10px', color: '#555' }}>Slide {currentSlideIndex + 1} of {slideshow.slides.length}</p>
        </div>
    );
};

export default StudentSlideshowPlayer;
